import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Paper, Typography, Button } from "@mui/material";
import { useTheme } from "../../theming";
import { useResponsive } from "../../contexts/ResponsiveContext";
import BusinessContextSection from "./BusinessContextSection";
import CompetitorAnalysisSection from "./CompetitorAnalysisSection";
import DesignPreferencesSection from "./DesignPreferencesSection";

const IndustryAnalysisDashboard = ({ startupName }) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [activeTab, setActiveTab] = useState(0);
  const [analysisData, setAnalysisData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch analysis data
  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await fetch(`/api/industry-analysis/${startupName}`);
        const data = await response.json();
        setAnalysisData(data);
      } catch (error) {
        console.error("Error fetching industry analysis:", error);
      }
    };
    fetchAnalysis();
  }, [startupName]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSave = async (updatedData) => {
    try {
      await fetch(`/api/industry-analysis/${startupName}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedData),
      });
      setAnalysisData(updatedData);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving industry analysis:", error);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        margin: "0 auto",
        px: { xs: 1, sm: 4, md: 6, lg: 8 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          backgroundColor: theme.backgroundColor,
          color: theme.textColor,
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          mx: { xs: 0 },
          borderRadius: { xs: 0, sm: 1 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            p: { xs: 2, sm: 3 },
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: theme.primaryColor,
              fontSize: { xs: "1.5rem", sm: "2rem" },
            }}
          >
            Industry Analysis Dashboard
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: { xs: "100%", sm: "auto" },
            }}
          ></Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="industry analysis tabs"
            sx={{
              "& .MuiTab-root": {
                color: theme.textColor,
                "&.Mui-selected": {
                  color: theme.primaryColor,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: theme.primaryColor,
              },
            }}
          >
            <Tab label="Business Context" />
            <Tab label="Competitor Analysis" />
            <Tab label="Design Preferences" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            {isEditing ? (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  sx={{
                    color: theme.textColor,
                    borderColor: theme.textColor,
                    "&:hover": {
                      borderColor: theme.primaryColor,
                      color: theme.primaryColor,
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleSave(analysisData);
                    setIsEditing(false);
                  }}
                  sx={{
                    backgroundColor: theme.primaryColor,
                    color: theme.buttonTextColor,
                    "&:hover": {
                      backgroundColor: `${theme.primaryColor}CC`,
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={() => setIsEditing(true)}
                sx={{
                  backgroundColor: theme.primaryColor,
                  color: theme.buttonTextColor,
                  "&:hover": {
                    backgroundColor: `${theme.primaryColor}CC`,
                  },
                }}
              >
                Edit
              </Button>
            )}
          </Box>
          {activeTab === 0 && (
            <BusinessContextSection
              data={analysisData?.businessContext}
              isEditing={isEditing}
              onSave={handleSave}
            />
          )}
          {activeTab === 1 && (
            <CompetitorAnalysisSection
              data={analysisData?.competitiveAnalysis}
              isEditing={isEditing}
              onSave={handleSave}
            />
          )}
          {activeTab === 2 && (
            <DesignPreferencesSection
              data={analysisData?.designPreferences}
              isEditing={isEditing}
              onSave={handleSave}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default IndustryAnalysisDashboard;
